import { useEffect, useState } from 'react';
import type { TeamJwt } from '../auth/teamJwt';
import { consoleConfig } from '../config';

type Preferences = { enabled: boolean; mentions: boolean; replies: boolean; results: boolean; deliveryConfigured: boolean };
const labels = { enabled: 'Email me about this team', mentions: 'Mentions', replies: 'Replies', results: 'Settled questions' } as const;

export function EmailNotificationPreferences({ orgId, getTeamJwt }: { orgId: string; getTeamJwt: () => Promise<TeamJwt> }) {
  const [open, setOpen] = useState(false);
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const endpoint = `${consoleConfig.collabApiBaseUrl}/api/teams/${encodeURIComponent(orgId)}/email-notifications`;
  useEffect(() => {
    if (!open) return;
    let active = true;
    setPreferences(null); setMessage('');
    void getTeamJwt().then((jwt) => fetch(endpoint, { headers: { Authorization: `Bearer ${jwt}` } }))
      .then(async (response) => { if (!response.ok) throw new Error('Unable to load email preferences.'); return response.json() as Promise<Preferences>; })
      .then((value) => { if (active) setPreferences(value); })
      .catch((error: Error) => { if (active) setMessage(error.message); });
    return () => { active = false; };
  }, [endpoint, getTeamJwt, open]);
  async function save() {
    if (!preferences || busy) return;
    setBusy(true); setMessage('');
    try {
      const { enabled, mentions, replies, results } = preferences;
      const jwt = await getTeamJwt();
      const response = await fetch(endpoint, { method: 'PUT', headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled, mentions, replies, results }) });
      if (!response.ok) throw new Error('Unable to save email preferences.');
      setPreferences(await response.json() as Preferences); setMessage('Saved');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'Unable to save email preferences.'); }
    finally { setBusy(false); }
  }
  return <details className="email-notification-preferences border-t border-nim px-3 py-2 text-xs text-nim-muted" onToggle={(event) => setOpen(event.currentTarget.open)}>
    <summary className="cursor-pointer">Email notifications</summary>
    {open && <div className="email-notification-options mt-2 grid gap-2">
      {!preferences && !message && <span>Loading preferences…</span>}
      {preferences && <>
        {!preferences.deliveryConfigured && <p>Email delivery is not enabled on this server yet.</p>}
        {Object.entries(labels).map(([key, label]) => <label key={key} className="flex items-center gap-2"><input type="checkbox" checked={preferences[key as keyof typeof labels]} disabled={busy || (key !== 'enabled' && !preferences.enabled)} onChange={(event) => setPreferences({ ...preferences, [key]: event.target.checked })} />{label}</label>)}
        <p>Addressed questions and reminders follow the team email setting.</p>
        <button className="rounded border border-nim px-2 py-1" type="button" disabled={busy} onClick={() => void save()}>{busy ? 'Saving…' : 'Save preferences'}</button>
      </>}
      {message && <span role="status">{message}</span>}
    </div>}
  </details>;
}
